/**
 * Unified Marketplace Layout
 * Base layout component for all marketplace categories
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Grid,
  Skeleton,
  Alert,
  Fab,
  Badge,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  ViewModule,
  ViewList,
  GridOn,
  Sort,
  FilterList,
  Compare,
  Favorite,
  ShoppingCart,
  Map,
  Share,
  Print
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchFilterPanel from './shared/SearchFilterPanel';
import ProductCard from './shared/ProductCard';
import VendorCard from './shared/VendorCard';
import { debounce } from '../../utils/helpers';

const MarketplaceLayout = ({
  category,
  title,
  description,
  data = [],
  loading = false,
  error = null,
  onSearch,
  onFilter,
  onSort,
  onLoadMore,
  totalResults = 0,
  currentPage = 1,
  totalPages = 1,
  children,
  showVendors = false,
  vendors = [],
  customActions = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    categories: [],
    priceRange: [0, 10000],
    location: [],
    rating: 0,
    features: []
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list' | 'compact'
  const [showFilters, setShowFilters] = useState(true);
  const [comparison, setComparison] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  // Debounced search
  const debouncedSearch = debounce((term) => {
    if (onSearch) onSearch(term);
  }, 300);

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
    }
  }, [searchTerm, debouncedSearch]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    if (onFilter) onFilter(newFilters);
  };

  // Handle sort changes
  const handleSortChange = (event) => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    if (onSort) onSort(newSortBy);
  };

  // Handle view mode changes
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode) {
      setViewMode(newViewMode);
    }
  };

  // Handle comparison
  const handleCompare = (item) => {
    setComparison(prev => {
      const isInComparison = prev.some(i => i.id === item.id);
      if (isInComparison) {
        return prev.filter(i => i.id !== item.id);
      } else if (prev.length < 3) {
        return [...prev, item];
      } else {
        // Replace first item if max reached
        return [item, ...prev.slice(1)];
      }
    });
  };

  // Handle wishlist
  const handleWishlist = (item) => {
    setWishlist(prev => {
      const isInWishlist = prev.some(i => i.id === item.id);
      if (isInWishlist) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  // Breadcrumb navigation
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [
      { label: 'Home', path: '/' },
      { label: 'Marketplace', path: '/marketplace' }
    ];

    if (pathSegments.length > 1) {
      breadcrumbs.push({
        label: title,
        path: `/marketplace/${category}`,
        current: true
      });
    }

    return breadcrumbs;
  };

  // Sort options
  const sortOptions = [
    { value: 'relevance', label: 'Most Relevant' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'newest', label: 'Newest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'distance', label: 'Nearest First' }
  ];

  // Speed dial actions
  const speedDialActions = [
    {
      icon: <Badge badgeContent={comparison.length} color="primary"><Compare /></Badge>,
      name: 'Compare',
      action: () => navigate(`/marketplace/${category}/compare`)
    },
    {
      icon: <Badge badgeContent={wishlist.length} color="error"><Favorite /></Badge>,
      name: 'Wishlist',
      action: () => navigate(`/marketplace/${category}/wishlist`)
    },
    {
      icon: <Map />,
      name: 'Map View',
      action: () => navigate(`/marketplace/${category}/map`)
    },
    {
      icon: <Share />,
      name: 'Share',
      action: () => navigator.share?.({ title, url: window.location.href })
    },
    ...customActions
  ];

  if (loading && !data.length) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ width: 300 }}>
            <Skeleton variant="rectangular" height={600} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
            <Grid container spacing={3}>
              {[...Array(8)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Skeleton variant="rectangular" height={350} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        {getBreadcrumbs().map((crumb, index) => (
          <Link
            key={index}
            color={crumb.current ? 'text.primary' : 'inherit'}
            href={crumb.path}
            onClick={(e) => {
              e.preventDefault();
              if (!crumb.current) navigate(crumb.path);
            }}
            sx={{ textDecoration: 'none', cursor: crumb.current ? 'default' : 'pointer' }}
          >
            {crumb.label}
          </Link>
        ))}
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={`${totalResults} results`} />
          {searchTerm && <Chip label={`"${searchTerm}"`} onDelete={() => setSearchTerm('')} />}
          {filters.categories?.length > 0 && (
            <Chip label={`${filters.categories.length} categories`} />
          )}
          {filters.location?.length > 0 && (
            <Chip label={`${filters.location.length} locations`} />
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Sidebar - Filters */}
        {showFilters && (
          <SearchFilterPanel
            category={category}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onSearch={onSearch}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            totalResults={totalResults}
            isLoading={loading}
          />
        )}

        {/* Main Content */}
        <Box sx={{ flexGrow: 1 }}>
          {/* Toolbar */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort by"
                  onChange={handleSortChange}
                  startAdornment={<Sort sx={{ mr: 1 }} />}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="grid">
                <ViewModule />
              </ToggleButton>
              <ToggleButton value="list">
                <ViewList />
              </ToggleButton>
              <ToggleButton value="compact">
                <GridOn />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Content Area */}
          {children || (
            <>
              {/* Products/Services Grid */}
              {data.length > 0 ? (
                <Grid container spacing={viewMode === 'compact' ? 2 : 3}>
                  {data.map((item) => (
                    <Grid 
                      item 
                      key={item.id}
                      xs={12}
                      sm={viewMode === 'list' ? 12 : viewMode === 'compact' ? 12 : 6}
                      md={viewMode === 'list' ? 12 : viewMode === 'compact' ? 6 : 4}
                      lg={viewMode === 'list' ? 12 : viewMode === 'compact' ? 4 : 3}
                    >
                      <ProductCard
                        product={item}
                        category={category}
                        onAddToCart={(product) => {/* Handle cart */}}
                        onAddToWishlist={handleWishlist}
                        onCompare={handleCompare}
                        isInWishlist={wishlist.some(w => w.id === item.id)}
                        isInComparison={comparison.some(c => c.id === item.id)}
                        loading={loading}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    No {category} found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Try adjusting your search criteria or filters
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSearchTerm('');
                      setFilters({
                        categories: [],
                        priceRange: [0, 10000],
                        location: [],
                        rating: 0,
                        features: []
                      });
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              )}

              {/* Vendors Section */}
              {showVendors && vendors.length > 0 && (
                <Box sx={{ mt: 6 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Featured Vendors
                  </Typography>
                  <Grid container spacing={3}>
                    {vendors.slice(0, 6).map((vendor) => (
                      <Grid item key={vendor.id} xs={12} md={6} lg={4}>
                        <VendorCard
                          vendor={vendor}
                          category={category}
                          layout="compact"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, page) => {
                      if (onLoadMore) onLoadMore(page);
                    }}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Speed Dial for Quick Actions */}
      <SpeedDial
        ariaLabel="Marketplace actions"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        icon={<SpeedDialIcon />}
      >
        {speedDialActions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            onClick={action.action}
          />
        ))}
      </SpeedDial>

      {/* Floating Action Buttons */}
      {comparison.length > 0 && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 100, right: 24 }}
          onClick={() => navigate(`/marketplace/${category}/compare`)}
        >
          <Badge badgeContent={comparison.length} color="error">
            <Compare />
          </Badge>
        </Fab>
      )}
    </Container>
  );
};

export default MarketplaceLayout;